import React, { useState } from 'react';
import { SyntaxLensIcon } from '../assets';

interface PasswordProtectionProps {
  onAuthenticated: () => void;
  correctPassword?: string;
}

const PasswordProtection: React.FC<PasswordProtectionProps> = ({ 
  onAuthenticated, 
  correctPassword = atob("SU5URUdFUg==") // Base64 encoded, still temporary
}) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Direct comparison - case sensitive
    if (password === correctPassword) {
      setTimeout(() => {
        setIsLoading(false);
        onAuthenticated();
      }, 500);
    } else {
      setTimeout(() => {
        setError(`Incorrect password. Please enter the exact text.`);
        setPassword('');
        setIsLoading(false);
      }, 500);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <img 
              src={SyntaxLensIcon} 
              alt="SyntaxLens" 
              className="h-16 w-16"
            />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            SyntaxLens Access
          </h2>
          <p className="text-gray-600">
            Enter the password to explore AI-powered code learning
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="password" className="sr-only">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="text"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value.toUpperCase())} // Auto-uppercase for consistency
              className="relative block w-full px-3 py-4 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:z-10 text-center text-2xl tracking-wider font-mono uppercase"
              placeholder="Enter password"
              disabled={isLoading}
              autoComplete="off"
              autoFocus
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded-lg border border-red-200">
              {error}
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoading || !password}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Authenticating...
                </div>
              ) : (
                'Access SyntaxLens'
              )}
            </button>
          </div>

          <div className="text-center text-xs text-gray-500 space-y-1">
            <p> AI-powered syntax learning</p>
            <p>Hover • Click • Double-click • Right-click to explore</p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasswordProtection;
